import { Dispatch, SetStateAction, useMemo, useRef, useState } from 'react'
import { Building } from 'models/Building'
import { useRecoilState, useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import currentViewerState from 'store/viewerStore'
import Krpano from 'utils/krpano'

export default function useThumbnailCarousel({
  building,
  setSelectedPointID
}: {
  building: Building | undefined
  setSelectedPointID: Dispatch<SetStateAction<string>>
}) {
  const carouselRef = useRef<HTMLDivElement>(null)
  const points = useRecoilValue(listPointState)
  const [isOpen, setIsOpen] = useState(true)

  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)

  /**
   * Handles the mousedown event on the thumbnail carousel by setting the
   * "isDragging" state to true, storing the initial x position of the mouse
   * relative to the carousel container, and setting the initial scroll left
   * position of the carousel. It also disables smooth scrolling and sets the
   * mouse cursor to "grabbing" during the drag operation.
   * @param {React.MouseEvent<HTMLDivElement>} e The mouse event.
   */
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (carouselRef.current) {
      setIsDragging(true)
      setStartX(e.pageX - carouselRef.current.offsetLeft)
      setScrollLeft(carouselRef.current.scrollLeft)
      carouselRef.current.style.scrollBehavior = 'auto' // Disable smooth scrolling during drag
      carouselRef.current.style.cursor = 'grabbing'
    }
  }

  /**
   * Handles the mousemove event on the thumbnail carousel by updating the
   * "scrollLeft" position of the carousel container based on the difference
   * between the current mouse x position and the initial x position of the
   * mouse. The "walk" value is multiplied by 2 to speed up the scrolling.
   * @param {React.MouseEvent<HTMLDivElement>} e The mouse event.
   */
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !carouselRef.current) return
    const currentX = e.pageX - carouselRef.current.offsetLeft
    const walk = (currentX - startX) * 2
    carouselRef.current.scrollLeft = scrollLeft - walk
  }

  /**
   * Handles the mouseup event on the thumbnail carousel by setting the
   * "isDragging" state to false, re-enabling smooth scrolling, and setting the
   * mouse cursor back to "grab". This is called when the user releases the
   * left mouse button after dragging the carousel.
   */
  const handleMouseUp = () => {
    setIsDragging(false)
    if (carouselRef.current) {
      carouselRef.current.style.scrollBehavior = 'smooth' // Enable smooth scrolling after drag
      carouselRef.current.style.cursor = 'grab'
    }
  }

  /**
   * Handles the mouseleave event on the thumbnail carousel by setting the
   * "isDragging" state to false, re-enabling smooth scrolling, and setting the
   * mouse cursor back to "grab". This is called when the user moves the mouse
   * cursor outside the carousel container while dragging the carousel.
   */
  const handleMouseLeave = () => {
    if (isDragging && carouselRef.current) {
      setIsDragging(false)
      carouselRef.current.style.scrollBehavior = 'smooth'
      carouselRef.current.style.cursor = 'grab'
    }
  }

  const directionList = useMemo(
    () =>
      currentViewer.pointDetail?.directions
        ?.map((item) => {
          const order = points.find(
            (p) =>
              p.id !== currentViewer.selectedPoint && item.id.includes(p.id)
          )?.order
          return !order ? { ...item, target: null } : { ...item, target: order }
        })
        .filter((item: any) => item.target),
    [currentViewer.pointDetail?.directions, currentViewer.selectedPoint, points]
  )

  const stairList = useMemo(
    () =>
      currentViewer.pointDetail?.stairways?.map((item) => ({
        ...item,
        nextFloorName: building?.floors.find(
          (floor) => floor.id === +item.nextFloorId
        )?.name
      })),
    [building?.floors, currentViewer.pointDetail?.stairways]
  )

  /**
   * When a thumbnail is clicked, this function is called with the ID of the point
   * represented by the thumbnail. The function sets the selected point ID to the
   * given point ID and calls `handleSelectPoint` with the given point ID.
   *
   * @param pointId The ID of the point represented by the clicked thumbnail.
   */
  const handleThumbnailClick = (pointId: string) => {
    setSelectedPointID(pointId)

    const tourViewerKrpano = document.getElementById('embedpano-viewer') as any
    const isLoadComplete = tourViewerKrpano?.get('loadcomplete')
    if (!tourViewerKrpano) return

    if (currentViewer.selectedPoint === pointId) {
      setCurrentViewer((prev) => ({
        ...prev,
        selectedImage: currentViewer.pointDetail?.image360?.id
      }))
      const currentSelectedPointImage = currentViewer.pointDetail?.image360

      const sceneName = Krpano.getScenceNameByFileId(
        currentViewer.selectedPoint,
        points,
        currentViewer.selectedImage
      )

      if (tourViewerKrpano.get(`scene[${sceneName}]`) === null) {
        const screenString = Krpano.generateSceneXML({
          sceneName,
          tilesUrl: currentSelectedPointImage?.tilesUrl || undefined,
          multires: currentSelectedPointImage?.multires || undefined,
          s3Path: currentSelectedPointImage?.s3Path || '',
          thumbnailUrl: currentSelectedPointImage?.multires
            ? `${currentSelectedPointImage?.tilesUrl}/preview.jpg`
            : currentSelectedPointImage?.thumbnail || ''
        })

        tourViewerKrpano.call(
          `includexmlstring(${screenString}, null, null, false)`
        )
      }

      tourViewerKrpano.call(
        `load_scene(${sceneName},${currentViewer.pointDetail?.initView?.h},${currentViewer.pointDetail?.initView?.v},${currentViewer.pointDetail?.initView?.z})`
      )

      const annotations = currentViewer.pointDetail?.annotations.filter(
        (item) => {
          return (
            !item.imageID ||
            item.imageID === currentViewer.pointDetail?.image360?.id
          )
        }
      )
      annotations && Krpano.AddAnnotation(tourViewerKrpano, annotations, null)
      directionList && Krpano.AddDirectionArrow(tourViewerKrpano, directionList)
      stairList && Krpano.AddStairway(tourViewerKrpano, stairList)
      return
    }

    if (currentViewer.selectedPoint !== pointId) {
      const selectedPointDetail = points.find((p) => p.id === pointId)
      setCurrentViewer((prev) => ({
        ...prev,
        selectedImage: selectedPointDetail?.image360?.id
      }))
    }

    if (isLoadComplete === undefined || isLoadComplete === 'true') {
      setCurrentViewer((prev) => ({
        ...prev,
        selectedPoint: pointId
      }))
    }
  }

  const handleSelectSubImage = (subImageId: number) => {
    const tourViewerKrpano = document.getElementById('embedpano-viewer') as any

    if (!tourViewerKrpano) return
    const currentSubImageDetails = currentViewer.pointDetail?.subImages?.find(
      (item) => item.id === subImageId
    )

    const sceneName = Krpano.getScenceNameByFileId(
      currentViewer.selectedPoint,
      points,
      subImageId
    )

    if (tourViewerKrpano.get(`scene[${sceneName}]`) === null) {
      const screenString = Krpano.generateSceneXML({
        sceneName,
        tilesUrl: currentSubImageDetails?.tilesUrl || undefined,
        multires: currentSubImageDetails?.multires || undefined,
        s3Path: currentSubImageDetails?.s3Path || '',
        thumbnailUrl: currentSubImageDetails?.multires
          ? `${currentSubImageDetails?.tilesUrl}/preview.jpg`
          : currentSubImageDetails?.thumbnail || ''
      })

      tourViewerKrpano.call(
        `includexmlstring(${screenString}, null, null, false)`
      )
    }

    tourViewerKrpano.call(
      `load_scene(${sceneName},${currentSubImageDetails?.initView?.h},${currentSubImageDetails?.initView?.v},${currentSubImageDetails?.initView?.z})`
    )

    const annotations = currentViewer.pointDetail?.annotations.filter(
      (item) => {
        return (
          !item.imageID ||
          item.imageID === currentViewer.pointDetail?.image360?.id
        )
      }
    )
    annotations && Krpano.AddAnnotation(tourViewerKrpano, annotations, null)
    directionList && Krpano.AddDirectionArrow(tourViewerKrpano, directionList)
    stairList && Krpano.AddStairway(tourViewerKrpano, stairList)
  }

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleThumbnailClick,
    isOpen,
    setIsOpen,
    handleSelectSubImage,
    currentViewer,
    setCurrentViewer,
    carouselRef
  }
}
